import React from "react";
import { GenericAlert } from "./generic-alert";
import { PostHogErrorBoundary, PostHogErrorBoundaryProps, usePostHog } from "posthog-js/react";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { CatchBoundary, useLocation } from "@tanstack/react-router";

interface GenericErrorDisplayProps {
  message?: string;
}

export const GenericErrorDisplay: React.FC<GenericErrorDisplayProps> = ({ message }) => (
  <div className="flex h-full w-full flex-col items-center justify-center">
    <GenericAlert>
      {message ? (
        <p className="mb-4 text-xl">{message}</p>
      ) : (
        <>
          <p className="mb-4 text-xl">
            Something's not working correctly, sorry about that!
          </p>
          <p className="mb-4">
            Try refreshing the page, which may resolve the issue.
          </p>
          <p className="mb-4">
            In the meantime, don't hesitate to reach out directly for what you
            need: partnerships@thalolabs.com{" "}
          </p>
        </>
      )}
      <Button
        size="lg"
        variant="thalo"
        onClick={() => window.location.reload()}
        className="flex items-center gap-2"
      >
        <RefreshCw size={16}/>
        Refresh page
      </Button>
    </GenericAlert>
  </div>
);

export const InstrumentedErrorBoundary: React.FC<PostHogErrorBoundaryProps> = ({ children, fallback }) => (
  <PostHogErrorBoundary fallback={fallback}>
    {children}
  </PostHogErrorBoundary>
);

export const InstrumentedErrorBoundaryGenericDisplay: React.FC<Omit<PostHogErrorBoundaryProps, "fallback">> = ({children}) => (
  <InstrumentedErrorBoundary fallback={<GenericErrorDisplay/>}>
    {children}
  </InstrumentedErrorBoundary>
);

export const InstrumentedCatchBoundaryGenericDisplay: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const posthog = usePostHog();
  const location = useLocation();

  return (
    <CatchBoundary
      getResetKey={() => location.pathname}
      onCatch={(error) => posthog.captureException(error)}
      errorComponent={GenericErrorDisplay}
    >
      {children}
    </CatchBoundary>
  );
};